import { useReducer } from 'react';
import { FormField, TextInput, Select } from 'grommet';
import { HelpWith } from '@components/basicComponents';
import { HelpWithType } from '@customTypes/enums';
import type { FormFieldsProps } from '@customTypes/types';
import type { GreenLakeCentralSupportFormFieldsState } from '@customTypes/stateTypes';

import {
  helpWithOptions,
  helpWithOptionsConsolidated,
  userManagementOptions,
  severityOptions,
} from './GreenLakeCentralSupportFormOptions';

export const GreenLakeCentralSupportFormFields = (props: FormFieldsProps): JSX.Element => {
  const [state, updateState] = useReducer(
  (prev: GreenLakeCentralSupportFormFieldsState, newState: Partial<GreenLakeCentralSupportFormFieldsState>) => ({
    ...prev,
    ...newState,
  }),
  {
    helpWith: '',
    helpWithSecondary: '',
    secondDropdownOptions: null,
    showTenantName: false,
    showAffectedUser: false,
    showSeverity: false,
  }
  );

  const helpWithChange = (option: string): void => {
  if (option === state.helpWith) {
    return;
  }

  clearFirstSelectState();
  updateState({ helpWith: option });

  switch (option) {
    case helpWithOptions.userManagement:
      updateState({ secondDropdownOptions: userManagementOptions });
      props.onDetailsChange({ numFiles: 0 });
      break;
    case helpWithOptions.api:
      updateState({ showSeverity: true });
      props.onDetailsChange({ showDetails: true, isRequiredDetails: true });
      break;
    default:
      updateState({ showSeverity: true });
      props.onDetailsChange({ showDetails: true, isRequiredDetails: true });
  }
  };

  const helpWithSecondaryChange = (option: string): void => {
    updateState({ helpWithSecondary: option, showTenantName: true, showAffectedUser: true, showSeverity: true });
    props.onDetailsChange({ showDetails: true, isRequiredDetails: true });
  };

  const clearFirstSelectState = (): void => {
    updateState({ helpWithSecondary: '', secondDropdownOptions: null, showTenantName: false, showAffectedUser: false, showSeverity: false });
    props.onDetailsChange({ showDetails: false });
  };

  const clearState = (): void => {
    clearFirstSelectState();
    updateState({ helpWith: '' });
  };

  props.onReset && props.onReset(clearState);

  return (
    <>
      <HelpWith
        selectType={!props.isConsolidated ? HelpWithType.First : HelpWithType.Second}
        options={!props.isConsolidated ? Object.values(helpWithOptions) : Object.values(helpWithOptionsConsolidated)}
        value={state.helpWith}
        onChange={helpWithChange}
      />

      {state.helpWith && state.secondDropdownOptions &&
        <HelpWith
          selectType={!props.isConsolidated ? HelpWithType.Second : HelpWithType.Third}
          options={Object.values(state.secondDropdownOptions)}
          value={state.helpWithSecondary}
          onChange={helpWithSecondaryChange}
        />
      }

      {state.showSeverity && props.isConsolidated &&
        <FormField name="severity" htmlFor="severity" label="Severity (business impact)" required>
          <Select id="severity" name="severity" placeholder="Select" options={severityOptions} />
        </FormField>
      }

      {state.helpWith === helpWithOptions.api &&
        <FormField name="apiName" htmlFor="api-name" label="API name" required>
          <TextInput id="api-name" name="apiName" placeholder="API name" />
        </FormField>
      }

      {state.showTenantName &&
        <FormField name="tenantName" htmlFor="tenant-name" label="Tenant name" required>
          <TextInput id="tenant-name" name="tenantName" placeholder="Tenant name" />
        </FormField>
      }

      {state.showAffectedUser &&
        <FormField
          name="affectedUser"
          htmlFor="affected-user"
          label="Affected user"
          required={state.helpWith === helpWithOptions.hpeGreenLakeFlex}
        >
          <TextInput id="affected-user" name="affectedUser" placeholder="Enter full name with email" />
        </FormField>
    }
  </>
  );
};
